///
/// EarthBrowser - Sentry Integration
///
/// Created: 2025
/// Copyright (c) 2025 Geodata Labs
///

import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import {
  useLocation,
  useNavigationType,
  matchRoutes,
  createRoutesFromChildren,
} from 'react-router-dom'
import { isNativeApp } from '@/native/platform'
import { isProduction, isDevelopment } from '@/utils/environment'
import { crashReportingAllowed } from '@/utils/privacy-consent'
import { logger } from '@/utils/LogDispatcher'
import { EmptyLogger } from '@stovokor/utils/logging'

// Initialize Sentry for error tracking and performance monitoring
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  // Forward logger warnings/errors to Sentry in production. Registered
  // here (not inside LogDispatcher) so the engine's loaders can log
  // without dragging the Sentry SDK into store-less builds (/embed)
  if (isProduction()) {
    logger.addLogger(new SentryLogger())
  }

  if (!dsn) {
    if (isDevelopment()) {
      console.log('[Sentry] No DSN configured, skipping initialization')
    }
    return
  }

  // Web: only with consent (GDPR). Native apps: crash reporting is on by
  // default, but Session Replay (screen capture) stays off there - it's
  // the one piece that goes beyond what a store crash-data disclosure
  // covers (see utils/privacy-consent.ts)
  const hasConsent = crashReportingAllowed()
  const replayAllowed = !isNativeApp()

  Sentry.init({
    dsn,
    environment: isProduction() ? 'production' : 'development',

    // Set release version for tracking deployments (from package.json via vite.config.ts)
    release: __APP_VERSION__,

    // Performance Monitoring
    tracesSampleRate: isProduction() ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session Replay - capture sessions where errors occur (web only)
    replaysSessionSampleRate: replayAllowed ? 0.1 : 0, // 10% of all sessions
    replaysOnErrorSampleRate: replayAllowed ? 1.0 : 0, // 100% of sessions with errors

    // Only enable if in production AND user has given consent
    enabled: isProduction() && hasConsent,

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      // Network errors that are expected
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // WebGL context lost (can happen on tab switch)
      'WebGL context lost',
      // User-initiated navigation
      'AbortError',
    ],

    // Filter out events from browser extensions
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-web-extension:\/\//i,
    ],

    // Add user context when available
    beforeSend(event) {
      // You can modify events here before sending
      // For example, strip PII if needed
      return event
    },

    integrations: [
      // React Router integration for better route tracking
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        matchRoutes,
        createRoutesFromChildren,
      }),
      // Session replay for debugging (web only)
      ...(replayAllowed
        ? [Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        })]
        : []),
    ],
  })
}

export function identifyUser(userId: string, email?: string, username?: string) {
  Sentry.setUser({
    id: userId,
    email,
    username,
  })
}

export function clearUser() {
  Sentry.setUser(null)
}

// Add breadcrumb for debugging
export function addBreadcrumb(message: string, category: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  })
}

// Forwards logger output to Sentry (breadcrumbs for info/warn, captured
// events for errors); moved here from LogDispatcher so that module stays
// free of the Sentry SDK
class SentryLogger extends EmptyLogger {
  override warn(message: string, ...params: unknown[]): void {
    Sentry.addBreadcrumb({
      category: 'warning',
      message,
      data: { params },
      level: 'warning',
    })
  }

  override error(error: unknown, ...params: unknown[]): void {
    if (error instanceof Error) {
      Sentry.captureException(error, {
        extra: { params },
      })
    } else {
      Sentry.captureMessage(String(error), {
        level: 'error',
        extra: { params },
      })
    }
  }

  override info(message: string, ...params: unknown[]): void {
    // Add breadcrumbs for important info messages
    Sentry.addBreadcrumb({
      category: 'info',
      message,
      data: { params },
      level: 'info',
    })
  }
}

// Re-export Sentry for use elsewhere
export { Sentry }
